<template>
    <div>
        <div class="workers-table-hashrate">
            <div class="workers-table-hashrate__line">
            <UIInfo
                class="workers-table-hashrate__info"
                v-show="getInfoIsShow"
                position="left"
            >
                <div class="workers-table-hashrate__info-list">
                    <div
                        class="workers-table-hashrate__info-item"
                        v-for="item in getInfoList"
                        :key="item.id"
                    >
                        <p class="workers-table-hashrate__info-label">
                            {{ item.label }}
                        </p>

                        <p class="workers-table-hashrate__info-label">
                            {{ item.value }}
                        </p>
                    </div>
                </div>
            </UIInfo>

            <p class="workers-table-hashrate__label">
                {{ worker.name }}

                <span
                    class="workers-table-hashrate__nominal"
                    v-if="getNominalIsShow"
                >
                    ({{ worker.nominal }} Th)
                </span>
            </p>

            <UIHashrate
                class="workers-table-hashrate__hashrate"
                v-if="getHashrateIsShow"
                :toggle="toggle"
                :disabled="getDisabled"
                @toggle="onToggle"
                @hashrate="onHashrate"
            />

            <button
                class="workers-table-hashrate__edit"
                v-if="getEditIsShow"
                @click="onEdit"
            >
                <UISvg class="workers-table-hasrate__edit-icon" icon="edit" />
            </button>
            </div>
        </div>

        <div class="workers-extra">
            <button type="button" class="workers-extra__title" @click="extraOpen = !extraOpen">
                {{ $t('workers.extraInfo') }}
                <span class="workers-extra__chevron" :class="{ 'workers-extra__chevron--open': extraOpen }"></span>
            </button>
            <p v-if="!extraOpen" class="workers-extra__summary">{{ equipmentSummary }}</p>
            <div v-if="extraOpen" class="workers-extra__grid">
                <label class="workers-extra__field">
                    <span>{{ $t('workers.reportBrand') }}</span>
                    <input v-model="equipment.brand" maxlength="128" type="text" />
                </label>
                <label class="workers-extra__field">
                    <span>{{ $t('workers.reportModel') }}</span>
                    <input v-model="equipment.model" maxlength="128" type="text" />
                </label>
                <label class="workers-extra__field">
                    <span>{{ $t('workers.reportSerial') }}</span>
                    <input v-model="equipment.serial" maxlength="128" type="text" />
                </label>
                <label class="workers-extra__field">
                    <span>{{ $t('workers.reportHours') }}</span>
                    <input v-model="equipment.hours" maxlength="7" type="text" inputmode="numeric" />
                </label>
            </div>
            <div v-if="extraOpen" class="workers-extra__actions">
                <button type="button" :disabled="equipmentSaving" @click="onSaveEquipment">
                    {{ equipmentSaving ? $t('workers.reportSaving') : $t('workers.reportSave') }}
                </button>
                <span v-if="equipmentSaved">{{ $t('workers.reportSaved') }}</span>
                <span v-if="equipmentError" class="workers-extra__error">{{ equipmentError }}</span>
            </div>
        </div>
        <div>
            <span v-for="tag of getTags" :key="tag.id" class="tag-container">
                <button class="tag-miner-button" disabled>
                    #{{tag.name}}
                </button>
            </span>
        </div>
    </div>
</template>

<script>
import UISvg from "@/components/ui/svg/svg.vue";
import UIHashrate from "@/components/ui/hashrate/hashrate.vue";
import UIInfo from "@/components/ui/info/info.vue";

import {getDate} from "@/utils/functions.js";

import Api from "@/api/api.js";

const api = new Api();

export default {
    name: "WorkersTableHashrate",
    components: {
        UISvg,
        UIHashrate,
        UIInfo,
    },
    props: {
        worker: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            toggle: false,
            loading: false,
            edit: false,
            equipment: {
                brand: "",
                model: "",
                serial: "",
                hours: "",
            },
            equipmentSaving: false,
            equipmentSaved: false,
            equipmentError: "",
            extraOpen: false,
        };
    },
    created() {
        this.syncEquipment();
    },
    watch: {
        "worker.equipmentBrand": "syncEquipment",
        "worker.equipmentModel": "syncEquipment",
        "worker.equipmentSerial": "syncEquipment",
        "worker.equipmentHours": "syncEquipment",
    },
    computed: {
        getTags() {
            return this.worker.tags
        },
        getNominalIsShow() {
            const {nominal} = this.worker;

            return nominal;
        },

        getHashrateIsShow() {
            const {nominal} = this.worker;

            return !nominal || this.edit;
        },

        getInfoIsShow() {
            return !this.toggle;
        },

        getDisabled() {
            return this.loading;
        },

        equipmentSummary() {
            const parts = [
                this.equipment.brand,
                this.equipment.model,
                this.equipment.serial,
                this.equipment.hours,
            ].map((part) => String(part || "").trim()).filter(Boolean);
            return parts.length ? parts.join(", ") : "—";
        },

        getEditIsShow() {
            const {nominal} = this.worker;

            return !this.edit && nominal;
        },

        getInfoList() {
            const {created, inetAddress, info, timeLastShare} = this.worker;

            return [
                {
                    id: 0,
                    label: this.$t('workers.connected') + ':',
                    value: getDate(created, "hh:mm:ss DD.MM.YYYY"),
                },
                {
                    id: 1,
                    label: this.$t('workers.ipAddress') + ':',
                    value: inetAddress,
                },
                {
                    id: 2,
                    label: this.$t('workers.infoLabel') + ':',
                    value: info,
                },
                {
                    id: 3,
                    label: this.$t('workers.lastShare') + ':',
                    value: getDate(timeLastShare, "hh:mm:ss DD.MM.YYYY"),
                },
            ];
        },
    },
    methods: {
        onToggle(toggle) {
            this.toggle = toggle;

            if (!toggle) {
                this.edit = false;
            }
        },

        async onHashrate(hashrate) {
            try {
                this.loading = true;

                await api
                    .nominal({
                        id: this.worker.id,
                        nominal: hashrate,
                    })
                    .then(async () => {
                        await api.workers().then((workers) => {
                            this.$emit("workers", workers);

                            this.loading = false;
                            this.toggle = false;
                            this.edit = false;
                        });
                    });
            } catch (e) {
                this.loading = false;
                this.toggle = false;
            }
        },

        onEdit() {
            this.toggle = true;
            this.edit = true;
        },

        syncEquipment() {
            this.equipment = {
                brand: this.worker.equipmentBrand || "",
                model: this.worker.equipmentModel || "",
                serial: this.worker.equipmentSerial || "",
                hours: this.worker.equipmentHours == null ? "" : String(this.worker.equipmentHours),
            };
        },

        async onSaveEquipment() {
            this.equipmentError = "";
            this.equipmentSaved = false;
            const hours = String(this.equipment.hours).trim();
            if (hours && !/^\d+$/.test(hours)) {
                this.equipmentError = this.$t("workers.reportError");
                return;
            }
            this.equipmentSaving = true;
            try {
                const body = await api.saveMinerEquipment({
                    id: this.worker.id,
                    brand: this.equipment.brand.trim(),
                    model: this.equipment.model.trim(),
                    serial: this.equipment.serial.trim(),
                    hours: hours ? Number(hours) : null,
                });
                if (!body || body.status !== "success") {
                    this.equipmentError = (body && body.message) || this.$t("workers.reportError");
                    return;
                }
                this.equipmentSaved = true;
                const workers = await api.workers();
                this.$emit("workers", workers);
            } catch (e) {
                this.equipmentError = this.$t("workers.reportError");
            } finally {
                this.equipmentSaving = false;
            }
        },
    },
};
</script>

<style lang="scss">
.workers-table-hashrate {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 8px;
    min-height: 28px;
    padding: 8px 12px;
}

.workers-table-hashrate__line {
    display: flex;
    align-items: center;
    min-height: 28px;
}

.workers-extra {
    width: 100%;
    max-width: 360px;

    .workers-extra__title {
        @include button-reset;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 11px;
        line-height: 14px;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: $txt-light;
        margin-bottom: 6px;
        cursor: pointer;
    }

    .workers-extra__chevron {
        width: 0;
        height: 0;
        border-left: 4px solid transparent;
        border-right: 4px solid transparent;
        border-top: 5px solid $txt-light;
        transition: transform 0.15s ease;
    }

    .workers-extra__chevron--open {
        transform: rotate(180deg);
    }

    .workers-extra__summary {
        font-size: 13px;
        line-height: 16px;
        color: $txt-main;
    }

    .workers-extra__grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 6px;
    }

    .workers-extra__field {
        display: flex;
        flex-direction: column;
        gap: 2px;

        span {
            font-size: 10px;
            line-height: 12px;
            color: $txt-light;
        }

        input {
            width: 100%;
            box-sizing: border-box;
            border: 1px solid $gray;
            border-radius: 8px;
            padding: 6px 8px;
            font-size: 12px;
            line-height: 16px;
            color: $txt-main;
            background: $white;
            outline: none;

            &:focus {
                border-color: $main;
            }
        }
    }

    .workers-extra__actions {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-top: 6px;
        font-size: 12px;
        color: $txt-light;

        button {
            @include button-reset;
            background: $main;
            color: $white;
            border-radius: 8px;
            padding: 6px 12px;
            font-size: 12px;
            cursor: pointer;

            &:disabled {
                opacity: 0.6;
                cursor: not-allowed;
            }
        }
    }

    .workers-extra__error {
        color: #e53935;
    }
}

.workers-table-hashrate {

    .workers-table-hashrate__edit {
        @include button-reset;
        margin-left: 4px;
    }

    .workers-table-hasrate__edit-icon {
        fill: $txt-light;
    }

    .workers-table-hashrate__label {
        font-size: 14px;
        line-height: 17px;
        color: $txt-main;
        white-space: nowrap;
    }

    .workers-table-hashrate__nominal {
        color: $txt-light;
    }

    .workers-table-hashrate__hashrate {
        margin-left: 6px;
    }

    .workers-table-hashrate__info {
        margin-right: 7px;
    }

    .workers-table-hashrate__info-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .workers-table-hashrate__info-label {
        font-size: 12px;
        line-height: 20px;
        color: $white;
    }
}

.tag-container {
  margin-left: 1px;
  margin-right: 1px;
}

.tag-miner-button {
  border: none;
  outline: none;
  padding: 0;
  transition: all 0.3s;
  font-family: "Golos-Regular", sans-serif;
  align-items: center;
  grid-column-gap: 14px;
  padding: 4px 8px;
  border: 1px solid #eceff0;
  border-radius: 18px;
  font-size: 12px;
  line-height: 12px;
  color: #222327;
  background: #c2fcd2;
}

@media screen and (max-width: 1024px) {
    .workers-table-hashrate {
        .workers-table-hashrate__hashrate {
            display: none;
        }
    }
}

@media screen and (max-width: 768px) {
    .workers-table-hashrate {
        padding: 0 8px;

        .workers-table-hashrate__nominal {
            display: none;
        }
    }
}
</style>
